import React, { memo, ReactNode, useState } from "react";

import { PencilFill as PencilIcon } from "@hl/base-components/lib/assets/icons.generated";
import { STATUS_COLOR } from "@hl/base-components/lib/theme/colors";
import { Menu, UnstyledButton } from "@mantine/core";
import { useNavigate } from "react-router-dom";

import { ReactComponent as Archive } from "assets/icons/archive.svg";
import { ReactComponent as Duplicate } from "assets/icons/copy-fill.svg";
import { ReactComponent as Delete } from "assets/icons/trash-fill.svg";
import { BlockchainEntity } from "components/BlockchainEntityTitle/types";
import { TOOLS_URL } from "config";
import { SubMenuItem } from "~/features/collections/sections/ContractCollectionDashboard/menu/BlockchainEntityMenu";
import DraftArchiveModal from "~/features/collections/sections/ContractCollectionDashboard/menu/DraftArchiveModal";
import DeleteDraftModal from "~/features/collections/sections/ContractCollectionDashboard/menu/DraftDeleteModal";
import DraftDuplicateModal from "~/features/collections/sections/ContractCollectionDashboard/menu/DraftDuplicateModal";
import { useArchiveDraft } from "~/features/collections/sections/ContractCollectionDashboard/menu/draftArchiveHook";

const DraftMenu = ({
  entity,
  menuButton,
}: {
  entity: BlockchainEntity;
  menuButton: ReactNode;
}) => {
  const navigate = useNavigate();
  const [deleteDraftModalOpened, setDeleteDraftModalOpened] = useState(false);
  const [archiveDraftModalOpened, setArchiveDraftModalOpened] = useState(false);
  const [duplicateDraftModalOpened, setDuplicateDraftModalOpened] =
    useState(false);
  const { toggleArchive } = useArchiveDraft({
    entity,
  });

  const items: SubMenuItem[] = [
    {
      label: "Continue creating",
      icon: <PencilIcon />,
      onClick: () =>
        navigate(
          TOOLS_URL.collections.resumeCreate.replace(":collectionId", entity.id)
        ),
    },
    ...(!entity.isArchive
      ? [
          {
            label: "Duplicate",
            icon: <Duplicate />,
            onClick: () => {
              setDuplicateDraftModalOpened(true);
            },
          },
        ]
      : []),
    {
      label: entity.isArchive ? "Unarchive" : "Archive",
      icon: <Archive />,
      onClick: () => {
        if (entity.isArchive) {
          return toggleArchive();
        }
        setArchiveDraftModalOpened(true);
      },
    },
    {
      label: "Delete",
      icon: <Delete />,
      color: STATUS_COLOR.ERROR,
      onClick: () => setDeleteDraftModalOpened(true),
    },
  ];
  return (
    <>
      <Menu
        position="bottom-end"
        width={200}
        styles={{ itemIcon: { width: 16, height: 16 } }}
      >
        <Menu.Target>
          <UnstyledButton>{menuButton}</UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown w={260}>
          {items.map((item) => {
            const { label, color, ...rest } = item;
            return (
              <Menu.Item
                key={`${entity.id}-${label}`}
                color={color}
                {...rest}
                sx={(theme) => ({
                  "&[data-hovered]": {
                    ...(color === STATUS_COLOR.ERROR && {
                      backgroundColor: theme.fn.themeColor(
                        STATUS_COLOR.ERROR_WASH
                      ),
                    }),
                  },
                })}
              >
                {label}
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>
      {deleteDraftModalOpened && (
        <DeleteDraftModal
          isOpened
          entity={entity}
          onClose={() => setDeleteDraftModalOpened(false)}
        />
      )}
      {archiveDraftModalOpened && (
        <DraftArchiveModal
          isOpened
          entity={entity}
          onClose={() => setArchiveDraftModalOpened(false)}
        />
      )}
      {duplicateDraftModalOpened && (
        <DraftDuplicateModal
          isOpened
          entity={entity}
          onClose={() => setDuplicateDraftModalOpened(false)}
        />
      )}
    </>
  );
};

export default memo(DraftMenu);
